import * as Sentry from "@sentry/nextjs";

import { isCrawler } from "~/utils/tracking/isCrawler";

type ErrorLike = Error | ErrorEvent | PromiseRejectionEvent;

const SKIP_SENTRY_TYPES = new Set([
  "errorUnhandled",
  "errorUnhandledRejection",
]);

const WARNING_CODES = new Set([
  "slowResponse",
  "slowPageGeneration",
  "page404",
  "page500",
  "emptyCatalog",
  "emptyMetaTitleOrDescription",
  "newMetaNotFound",
  "newMetaError",
  "newTextMismatch",
  "newTextError",
  "errorImagesLoading",
  "orderError",
  // Сервер не принял заявку — чаще всего не заполнено обязательное поле
  "formRejected",
]);

const WARNING_TYPES = new Set(["formLogger"]);

// Заявка или заказ потеряны: сервер ответил пустым ответом, клиент ушёл ни с чем.
// Самый высокий уровень — восстанавливать по log/leads.log Битрикса.
const FATAL_CODES = new Set(["orderInitFailed", "formEmptyResponse"]);

const API_ERROR_TYPES = new Set(["apiWrapperCallError", "apiCallError"]);

// Коды, которые не имеет смысла присылать от поисковых ботов/краулеров:
// они массово обходят каталог, тянут картинки пачками и раздувают счётчик
// шумом, не отражающим реальные проблемы пользователей.
const SKIP_FOR_CRAWLER_CODES = new Set(["errorImagesLoading"]);

function getRequestMethod(details: Record<string, unknown>) {
  if (typeof details.method === "string") {
    return details.method.toUpperCase();
  }
  if (typeof details.type === "string") {
    return details.type.toUpperCase();
  }
  return "UNKNOWN";
}

/**
 * Код ответа для группировки. Без него все неудачные ответы одного URL
 * попадали в одну issue: 403 от защиты периметра лежал вперемешку с 503 от
 * перегруженного бэкенда, а заголовок issue брался от первого события — то
 * есть по названию было не понять, что внутри две разные проблемы.
 */
function getStatusKey(details: Record<string, unknown>) {
  const { status } = details;
  if (typeof status === "number" || typeof status === "string") {
    return String(status);
  }
  return "noStatus";
}

function getApiFingerprint(
  type: string,
  code: string,
  details: Record<string, unknown>,
) {
  if (!API_ERROR_TYPES.has(type)) {
    return undefined;
  }

  const url = typeof details.url === "string" ? details.url : "unknown";
  return [type, code, getRequestMethod(details), url, getStatusKey(details)];
}

function getSentryTags(
  type: string,
  code: string,
  details: Record<string, unknown>,
) {
  const tags: Record<string, string> = { errorType: type, errorCode: code };

  if (API_ERROR_TYPES.has(type) && typeof details.url === "string") {
    tags.apiUrl = details.url;
    // Тег статуса — чтобы в GlitchTip можно было фильтровать (status:503) и
    // сразу видеть код в списке событий, не открывая каждое.
    tags.status = getStatusKey(details);
  }
  if (type === "ssrError") {
    tags.source = "sspWrapper";
  }

  return tags;
}

function extractError(event: ErrorLike | null): Error | null {
  if (!event) {
    return null;
  }
  if (event instanceof Error) {
    return event;
  }
  if (typeof ErrorEvent !== "undefined" && event instanceof ErrorEvent) {
    if (event.error instanceof Error) {
      return event.error;
    }
    return new Error(event.message);
  }
  if (
    typeof PromiseRejectionEvent !== "undefined" &&
    event instanceof PromiseRejectionEvent
  ) {
    if (event.reason instanceof Error) {
      return event.reason;
    }
    return new Error(String(event.reason));
  }
  return null;
}

export function reportToSentry(
  type = "",
  code = "",
  details: Record<string, unknown> = {},
  event: ErrorLike | null = null,
) {
  if (SKIP_SENTRY_TYPES.has(type)) {
    return;
  }

  if (SKIP_FOR_CRAWLER_CODES.has(code) && isCrawler()) {
    return;
  }

  try {
    const tags = getSentryTags(type, code, details);
    const extra = { ...details };
    const error = extractError(event);
    const fingerprint = getApiFingerprint(type, code, details);

    if (type === "reactErrorBoundary" && error) {
      Sentry.captureException(error, {
        tags,
        extra,
        fingerprint,
        contexts: {
          react: {
            componentStack: details.componentStack as string | undefined,
          },
        },
      });
      return;
    }

    if (WARNING_CODES.has(code) || WARNING_TYPES.has(type)) {
      Sentry.captureMessage(`${type}: ${code}`, {
        level: "warning",
        tags,
        extra,
        // Стабильный fingerprint, чтобы все одинаковые предупреждения
        // (напр. errorImagesLoading) копились в одной issue, а не плодили новые.
        fingerprint: fingerprint ?? [type, code],
      });
      return;
    }

    if (error) {
      Sentry.captureException(error, {
        tags,
        extra,
        fingerprint,
      });
      return;
    }

    Sentry.captureMessage(`${type}: ${code}`, {
      level: FATAL_CODES.has(code) ? "fatal" : "error",
      tags,
      extra,
      // Как у предупреждений: без него GlitchTip раскладывал одну и ту же ошибку
      // по поддоменам — cartError: orderRejected 15.10.2026 дал 5 issue.
      fingerprint: fingerprint ?? [type, code],
    });
  } catch (reportError) {
    console.error("[reportToSentry] failed to send event to GlitchTip", {
      type,
      code,
      details,
      reportError,
    });
  }
}
